import { useEffect, useState } from "react";
import { RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { money, day } from "@/lib/money";

export default function RenewalCard({ api }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/billing/renewal").then(({ data }) => setPlan(data.plan)).catch(() => {});
  }, [api]);

  if (!plan) return null;

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/renewal", { enabled, origin_url: window.location.origin });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setPlan(data.plan);
      toast.success(enabled ? "Auto-renew is on" : "Auto-renew is off. Your plan stays active until its end date.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update auto-renew. Try again.");
    }
    setBusy(false);
  };

  const on = plan.auto_renew;
  return (
    <div className="brutal-border p-4 sm:p-5 mb-8 max-w-xl bg-[#0A0A0A]" data-testid="renewal-card">
      <div className="font-mono-label text-[10px] text-muted-foreground inline-flex items-center gap-1.5">
        <RefreshCw size={12} className="text-[#FFD600]" /> YOUR CURRENT PLAN
      </div>
      <div className="mt-2 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="font-heading font-black text-xl truncate" data-testid="renewal-plan-name">{plan.name}</div>
          <p className="text-sm text-muted-foreground mt-1" data-testid="renewal-summary">
            {on ? `Renews ${day(plan.ends_at)} · ${money(plan.amount_cents)} will be charged`
              : `Ends ${day(plan.ends_at)} · renews at ${money(plan.amount_cents)}`}
          </p>
        </div>
        {plan.can_auto_renew && (
          <label className="flex items-center gap-2 shrink-0 font-mono-label text-[11px]">
            {busy ? <Loader2 size={13} className="animate-spin" /> : null}
            AUTO-RENEW
            <Switch checked={on} disabled={busy} onCheckedChange={toggle} data-testid="renewal-auto-renew-switch"
              className="data-[state=checked]:bg-[#FFD600] data-[state=unchecked]:bg-[#262626]" />
          </label>
        )}
      </div>
      {!on && plan.kind === "one_time" && plan.can_auto_renew && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid="renewal-one-time-note">
          Turning this on saves your card. You won't be charged until your plan ends.
        </p>
      )}
    </div>
  );
}
